import React from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput } from '@/components/forms/validation';
import { getInputValue } from '@/utils/html';
import { BaseProduct, ProductSourceCatalog, ProfileCatalogProduct } from '@/types';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import theme from './EditProductWizard.module.scss';
import { useGetActiveProfile, useGetActiveProject } from '@/utils/hooks';
import { Measurements, unitAbbreviations } from '@/utils/unit-of-measurements';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';

interface EditProductWizardStepInfoProps {
    product: BaseProduct;
    onChange: (product: BaseProduct) => void;
    productMeasurements: Measurements;
    onProductMeasurementsChange(measurements: Measurements): void;
    allowPriceEdit: boolean;
    sourceCatalog: ProductSourceCatalog;
}

const EditProductWizardStepInfo: React.FC<EditProductWizardStepInfoProps> = ({
    product,
    onChange,
    allowPriceEdit,
    productMeasurements,
    onProductMeasurementsChange,
    sourceCatalog,
}) => {
    const handleInputChange = (e) => {
        onChange({ ...product, [e.target.name]: getInputValue(e.target) });
    };
    const handleMeasurementsChange = (e) => {
        onProductMeasurementsChange({ ...productMeasurements, [e.target.name]: getInputValue(e.target) });
    };

    const profile = useGetActiveProfile();
    const project = useGetActiveProject();

    if (!profile && !project) {
        return <LoadingIndicator />;
    }

    const unitAbbreviation =
        unitAbbreviations[
        sourceCatalog === ProductSourceCatalog.Profile
            ? profile!.defaultUnitOfMeasurement
            : project!.defaultUnitOfMeasurement
        ];

    return (
        <div className="form columns-2">
            <div>
                {allowPriceEdit && (
                    <label>
                        <div className={theme.titleContainer}>
                            <Trans>Suggested price</Trans>
                            <InfoIconWithTooltip
                                content={
                                    <Trans>
                                        This price is for your own list price. The price to the buyer is decided when you
                                        offer the product at a later stage. Only people from your organization are able to
                                        view your price in the catalog.
                                    </Trans>
                                }
                            />
                        </div>

                        <ValidatingInput
                            autoFocus
                            className="form-control"
                            type="number"
                            value={(product as ProfileCatalogProduct).defaultPrice || ''}
                            onChange={handleInputChange}
                            name="defaultPrice"
                            placeholder={t`Price`}
                        />
                    </label>
                )}

                <label>
                    <div className={theme.titleContainer}>
                        <Trans>Width ({unitAbbreviation})</Trans>
                    </div>

                    <ValidatingInput
                        className="form-control"
                        type="number"
                        value={productMeasurements.width || ''}
                        onChange={handleMeasurementsChange}
                        name="width"
                        placeholder={t`Width`}
                    />
                </label>

                <label>
                    <div className={theme.titleContainer}>
                        <Trans>Height ({unitAbbreviation})</Trans>
                    </div>

                    <ValidatingInput
                        className="form-control"
                        type="number"
                        value={productMeasurements.height || ''}
                        onChange={handleMeasurementsChange}
                        name="height"
                        placeholder={t`Height`}
                    />
                </label>
            </div>

            <div>
                <label>
                    <div className={theme.titleContainer}>
                        <Trans>Product ID</Trans>
                        <InfoIconWithTooltip content={<Trans>The ID you use to identify this product</Trans>} />
                    </div>

                    <ValidatingInput
                        className="form-control"
                        type="text"
                        value={product.sku || ''}
                        onChange={handleInputChange}
                        name="sku"
                        placeholder={t`Product ID`}
                    />
                </label>
                <label>
                    <div className={theme.titleContainer}>
                        <Trans>Depth ({unitAbbreviation})</Trans>
                    </div>

                    <ValidatingInput
                        className="form-control"
                        type="number"
                        value={productMeasurements.depth || ''}
                        onChange={handleMeasurementsChange}
                        name="depth"
                        placeholder={t`Depth`}
                    />
                </label>

                <label>
                    <div className={theme.titleContainer}>
                        <Trans>Length ({unitAbbreviation})</Trans>
                    </div>

                    <ValidatingInput
                        className="form-control"
                        type="number"
                        value={productMeasurements.length || ''}
                        onChange={handleMeasurementsChange}
                        name="length"
                        placeholder={t`Length`}
                    />
                </label>
            </div>
        </div>
    );
};

export default EditProductWizardStepInfo;